import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useSession } from '../../data/session';
import { getHeadedOutProvider } from './data';
import { toDisplayRows } from './format';
import type { HeadedOutRow } from './types';

type Props = {
  autoScrollIntoView?: boolean;
};

export function HeadedOutList({ autoScrollIntoView }: Props) {
  const { familyId } = useSession();
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<HeadedOutRow[]>([]);
  const [familyMap, setFamilyMap] = useState<Record<string, string>>({});
  const [parkMap, setParkMap] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLElement>(null);

  // Fetch function (extract maps if provider exposes them)
  async function fetchRows() {
    const p = getHeadedOutProvider() as any;
    try {
      const data = await p.listActiveUpcoming();
      const fam = typeof p.getLastFamilyMap === 'function' ? p.getLastFamilyMap() : {};
      const par = typeof p.getLastParkMap === 'function' ? p.getLastParkMap() : {};
      setFamilyMap(fam);
      setParkMap(par);
      setRows(data);
    } catch {
      // Leave rows as-is on failure
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchRows();
    const onFocus = () => void fetchRows();
    const onVis = () => {
      if (document.visibilityState === 'visible') void fetchRows();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    const it = setInterval(() => void fetchRows(), 60_000);
    // Refresh after a post from AnnounceSheet
    const onPosted = () => void fetchRows();
    window.addEventListener('headedout:posted', onPosted as any);
    // Refresh + scroll when SW asks to navigate to /headed-out, even if already on it
    const onSwNavigate = (e: any) => {
      const url = e?.detail?.url as string | undefined;
      if (url && url.startsWith('/headed-out')) {
        try {
          containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch {}
        void fetchRows();
      }
    };
    window.addEventListener('headedout:navigate', onSwNavigate as any);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(it);
      window.removeEventListener('headedout:posted', onPosted as any);
      window.removeEventListener('headedout:navigate', onSwNavigate as any);
    };
  }, []);

  useEffect(() => {
    if (autoScrollIntoView && containerRef.current) {
      try {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        // ignore
      }
    }
  }, [autoScrollIntoView]);

  const displayRows = useMemo(
    () => toDisplayRows(rows, familyMap, parkMap, familyId),
    [rows, familyMap, parkMap, familyId]
  );

  async function onAction(id: string, action: 'left' | 'cancelled') {
    // Optimistic remove
    setRows((rs) => rs.filter((r) => r.id !== id));
    try {
      await getHeadedOutProvider().endAnnouncement(id, action);
    } catch {
      // On failure, refetch to reconcile
      void fetchRows();
    }
  }

  return (
    <section ref={containerRef as any} class="headedout">
      <h2 class="headedout-title">Who's headed out</h2>
      {loading ? (
        <p class="muted">Loading…</p>
      ) : displayRows.length === 0 ? (
        <p class="muted" aria-live="polite">
          Nobody's headed out right now.
        </p>
      ) : (
        <ul class="headedout-list">
          {displayRows.map((r) => (
            <li key={r.id} class="headedout-item">
              <div class="headedout-main">
                <div class="headedout-names">
                  <span class="headedout-family">{r.familyName}</span>{' '}
                  <span class="headedout-park">at {r.parkName}</span>
                </div>
                <div class="headedout-when">{r.whenLabel}</div>
              </div>
              {r.action.kind !== 'none' && (
                <div class="headedout-action">
                  <button
                    class={r.action.kind === 'cancel' ? 'btn-danger' : 'btn-secondary'}
                    type="button"
                    onClick={() => onAction(r.id, r.action.kind === 'cancel' ? 'cancelled' : 'left')}
                    aria-label={r.action.label}
                  >
                    {r.action.label}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

